export type Scope = Record<string, Record<string, string[]>> | null | undefined;

/**
 * Checks if the user's scope satisfies the minimum required scope for a
 * resource/action combination.
 *
 * Hierarchical semantics: `'all'` implies `'own'`, so a scope of `['all']`
 * satisfies `minScope: 'own'`, but `['own']` does NOT satisfy `minScope: 'all'`.
 *
 * Kept in sync with `packages/backend/src/utils/permissions.js`.
 *
 * @param scope - The user's scope object from authentication context
 * @param resource - The resource name (e.g., "claims", "users", "balances")
 * @param action - The action name (e.g., "view", "approve", "create")
 * @param minScope - The minimum scope level required (e.g., "own", "all")
 * @returns True if the user has the required permission, false otherwise
 *
 * @example
 * ```typescript
 * // Check if user can view their own claims (admin with ['all'] also passes)
 * hasPermission(scope, 'claims', 'view', 'own')
 *
 * // Check if user can approve all claims
 * hasPermission(scope, 'claims', 'approve', 'all')
 * ```
 */
export function hasPermission(
  scope: Scope,
  resource: string,
  action: string,
  minScope: string,
): boolean {
  const actionScopes = scope?.[resource]?.[action];
  if (!Array.isArray(actionScopes) || actionScopes.length === 0) {
    return false;
  }
  if (minScope === 'all') {
    return actionScopes.includes('all');
  }
  if (minScope === 'own') {
    return actionScopes.includes('own') || actionScopes.includes('all');
  }
  return actionScopes.includes(minScope);
}

/**
 * Checks if the user can access a resource with any scope level.
 *
 * This function checks if the user has any permission for a resource and action,
 * regardless of the scope level (own, all, etc.). Useful for determining if
 * a user can access a feature at all.
 *
 * @param scope - The user's scope object from authentication context
 * @param resource - The resource name (e.g., "claims", "users", "balances")
 * @param action - The action name (e.g., "view", "approve", "create")
 * @returns True if the user has any access level, false otherwise
 *
 * @example
 * ```typescript
 * // Check if user can view claims (own or all)
 * canAccess(scope, 'claims', 'view')
 * ```
 */
export function canAccess(scope: Scope, resource: string, action: string): boolean {
  const actionScopes = scope?.[resource]?.[action];
  return Array.isArray(actionScopes) && actionScopes.length > 0;
}
